import { useState } from "react";

import { SWReactCountryIcons } from "@similarweb/icons";
import { FlexRow, ItemIcon, Tooltip, Type } from "@similarweb/ui-components";

import { translate } from "app/filters/i18nFilter";
import { ACTIVATION_SHARED_TOOLTIP } from "app/pages/activation/components/Header/texts";
import { PrimaryVersus } from "app/pages/activation/components/Shared/PrimaryVersus/PrimaryVersus";
import {
    ACTIVATION_PROJECT_ROUTE,
    DEFAULT_ACTIVATION_GRANULARITY,
    DEFAULT_ACTIVATION_WEB_SOURCE,
} from "app/pages/activation/routing/constants";
import { activationTracking } from "app/pages/activation/services/tracking";
import { proNavigator } from "single-spa/react-app/ProNavigator";
import CountryService from "app/services/CountryService";

import HoverIcons from "./HoverIcons";
import * as SC from "./styles";
import { activationItemTestKeys } from "./testKeys";
import type { ActivationItemSchema } from "./types";

export const ActivationItem = ({
    name,
    countryId,
    sharedBy,
    primaryDomain,
    isSelected,
    competitors,
    favicons,
    id,
    currentId,
    onDefaultPageSelected,
    isDefaultPageItem,
    isOwn,
    activeId,
}: ActivationItemSchema) => {
    const [isHovered, setIsHovered] = useState(false);

    const track = activationTracking.home;

    const onClick = () => {
        if (id !== currentId) {
            track.trackSelectActivationFromDD();

            proNavigator.go(ACTIVATION_PROJECT_ROUTE, {
                key: id,
                country: countryId,
                webSource: DEFAULT_ACTIVATION_WEB_SOURCE,
                timeGranularity: DEFAULT_ACTIVATION_GRANULARITY,
            });
        }
    };

    const onSetDefaultClick = (e: MouseEvent) => {
        e.stopPropagation();

        onDefaultPageSelected(id, name, countryId);
    };

    const countryText = CountryService.getCountryById(countryId)?.text;

    return (
        <SC.ItemWrapper
            key={id}
            isSelected={isSelected}
            isActive={id === activeId}
            onClick={onClick}
            data-automation={activationItemTestKeys.item}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <SC.LeftSide>
                <ItemIcon
                    iconType={Type.Website}
                    iconSrc={favicons[primaryDomain]}
                />
                <SC.DataWrapper>
                    <FlexRow>
                        <SC.Name>{name}</SC.Name>

                        {!isOwn ? (
                            <Tooltip
                                enabled={!!sharedBy}
                                content={translate(ACTIVATION_SHARED_TOOLTIP, { name: sharedBy })}
                            >
                                <div data-automation={activationItemTestKeys.sharedByIcon}>
                                    <SC.SharedIcon
                                        iconName="social-share"
                                        size="xs"
                                    />
                                </div>
                            </Tooltip>
                        ) : null}
                    </FlexRow>

                    <SC.CustomSecondRow>
                        <PrimaryVersus
                            competitors={competitors}
                            favicons={favicons}
                            primaryDomain={primaryDomain}
                        />
                        <SC.CountryWrapper>
                            <SWReactCountryIcons
                                countryCode={countryId}
                                size="xs"
                            />
                            <SC.CountryText>{countryText}</SC.CountryText>
                        </SC.CountryWrapper>
                    </SC.CustomSecondRow>
                </SC.DataWrapper>
            </SC.LeftSide>

            <HoverIcons
                isSelected={isSelected}
                isHovered={isHovered}
                isDefaultPageItem={isDefaultPageItem}
                onClick={onSetDefaultClick}
            />
        </SC.ItemWrapper>
    );
};

